<template>
  <div class="vc-triple-access">
    <!-- 语音合成 TTS -->
    <div class="vc-access-block">
      <div class="vc-access-block__title">语音合成 (TTS)</div>
      <div class="vc-access-fields-row">
        <div class="vc-access-field-block">
          <label class="vc-form-label">资源</label>
          <div class="vc-resource-select-row">
            <Select
              ref="ttsEngineSelect"
              v-model="tripleAccess.tts.engine"
              class="vc-form-control vc-grouped-select"
              filterable
              transfer
              transfer-class-name="vc-grouped-select-transfer"
              placeholder="请选择"
              :loading="ttsListLoading"
              @on-change="onTtsEngineChange"
              @on-open-change="(open) => onTripleSelectOpenChange(open, 'ttsEngineSelect')"
            >
              <OptionGroup label="系统资源">
                <Option
                  v-for="item in ttsDictionaryResourceOptions"
                  :key="'dict-' + item.value"
                  :value="item.value"
                  :label="item.label"
                >
                  {{ item.label }}
                </Option>
              </OptionGroup>
              <OptionGroup v-if="ttsCustomResourceOptions.length" label="我的资源">
                <Option
                  v-for="item in ttsCustomResourceOptions"
                  :key="'custom-' + item.value"
                  :value="item.value"
                  :label="item.label"
                >
                  {{ item.label }}
                </Option>
              </OptionGroup>
            </Select>
            <Button class="vc-my-resources-btn" @click="openMyTtsResources()">
              <span class="vc-my-resources-btn__content">
                <Icon type="ios-folder-outline" size="16" />
                <span class="vc-my-resources-btn__label">我的资源</span>
              </span>
            </Button>
          </div>
        </div>
        <div class="vc-access-field-block">
          <label class="vc-form-label">音色</label>
          <div class="vc-voice-select-row">
            <Select
              ref="ttsVoiceSelect"
              v-model="tripleAccess.tts.voice_id"
              class="vc-form-control vc-grouped-select"
              filterable
              allow-create
              transfer
              transfer-class-name="vc-grouped-select-transfer"
              placeholder="请选择音色"
              :disabled="!tripleTtsVoiceOptions.length && !ttsListLoading && !isCustomTtsResource"
              :loading="ttsListLoading"
              @on-create="addTripleTtsSpeaker"
              @on-open-change="(open) => onTripleSelectOpenChange(open, 'ttsVoiceSelect')"
            >
              <OptionGroup label="系统预设">
                <Option
                  v-for="item in tripleTtsSystemVoiceOptions"
                  :key="'preset-' + item.value"
                  :value="item.value"
                  :label="item.label || item.value"
                >
                  {{ item.label || item.value }}
                </Option>
              </OptionGroup>
              <OptionGroup v-if="tripleTtsClonedVoiceOptions.length" label="我的音色">
                <Option
                  v-for="item in tripleTtsClonedVoiceOptions"
                  :key="'clone-' + item.value"
                  :value="item.value"
                  :label="item.label || item.value"
                >
                  {{ item.label || item.value }}
                </Option>
              </OptionGroup>
            </Select>
            <Button class="vc-my-voices-btn" @click="$emit('open-my-voices')">
              <span class="vc-my-voices-btn__content">
                <Icon type="ios-mic-outline" size="16" />
                <span class="vc-my-voices-btn__label">我的音色</span>
              </span>
            </Button>
          </div>
        </div>
      </div>
      <SpeechParamsSection
        v-if="ttsSpeechParams"
        :speech-params="ttsSpeechParams"
        :max-params="maxSpeechParams"
        class="vc-access-params"
      />
    </div>

    <!-- 大语言模型 LLM -->
    <div class="vc-access-block">
      <div class="vc-access-block__title">大语言模型 (LLM)</div>
      <div class="vc-access-fields-row">
        <div class="vc-access-field-block">
          <label class="vc-form-label">资源</label>
          <div class="vc-resource-select-row">
            <Select
              ref="llmResourceSelect"
              v-model="tripleAccess.llm.resource"
              class="vc-form-control vc-grouped-select"
              filterable
              transfer
              transfer-class-name="vc-grouped-select-transfer"
              placeholder="请选择"
              @on-change="onLlmResourceChange"
              @on-open-change="(open) => onTripleSelectOpenChange(open, 'llmResourceSelect')"
            >
              <OptionGroup label="系统资源">
                <Option
                  v-for="item in llmSystemResourceOptions"
                  :key="'system-' + item.value"
                  :value="item.value"
                  :label="item.label"
                >
                  {{ item.label }}
                </Option>
              </OptionGroup>
              <OptionGroup v-if="llmCustomModelOptions.length" label="我的资源">
                <Option
                  v-for="item in llmCustomModelOptions"
                  :key="'custom-' + item.value"
                  :value="item.value"
                  :label="item.label"
                >
                  {{ item.label }}
                </Option>
              </OptionGroup>
            </Select>
            <Button class="vc-my-resources-btn" @click="openMyLlmResources()">
              <span class="vc-my-resources-btn__content">
                <Icon type="ios-folder-outline" size="16" />
                <span class="vc-my-resources-btn__label">我的资源</span>
              </span>
            </Button>
          </div>
        </div>
        <div class="vc-access-field-block">
          <label class="vc-form-label">模型</label>
          <Select
            v-model="tripleAccess.llm.model"
            class="vc-form-control"
            transfer
            placeholder="请选择"
            :disabled="!llmModelSelectOptions.length"
          >
            <Option
              v-for="item in llmModelSelectOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              {{ item.label }}
            </Option>
          </Select>
        </div>
      </div>
    </div>

    <!-- 语音识别 ASR -->
    <div class="vc-access-block">
      <div class="vc-access-block__title">语音识别 (ASR)</div>
      <div class="vc-access-fields-row">
        <div class="vc-access-field-block">
          <label class="vc-form-label">资源</label>
          <div class="vc-resource-select-row">
            <Select
              ref="asrEngineSelect"
              v-model="tripleAccess.asr.engine"
              class="vc-form-control vc-grouped-select"
              filterable
              transfer
              transfer-class-name="vc-grouped-select-transfer"
              placeholder="请选择"
              :loading="asrListLoading"
              @on-open-change="(open) => onTripleSelectOpenChange(open, 'asrEngineSelect')"
            >
              <OptionGroup label="系统资源">
                <Option
                  v-for="item in asrDictionaryResourceOptions"
                  :key="'dict-' + item.value"
                  :value="item.value"
                  :label="item.label"
                >
                  {{ item.label }}
                </Option>
              </OptionGroup>
              <OptionGroup v-if="asrCustomResourceOptions.length" label="我的资源">
                <Option
                  v-for="item in asrCustomResourceOptions"
                  :key="'custom-' + item.value"
                  :value="item.value"
                  :label="item.label"
                >
                  {{ item.label }}
                </Option>
              </OptionGroup>
            </Select>
            <Button class="vc-my-resources-btn" @click="openMyAsrResources()">
              <span class="vc-my-resources-btn__content">
                <Icon type="ios-folder-outline" size="16" />
                <span class="vc-my-resources-btn__label">我的资源</span>
              </span>
            </Button>
          </div>
        </div>
        <div class="vc-access-field-block vc-access-field-block--placeholder" aria-hidden="true" />
      </div>
      <SpeechParamsSection
        v-if="asrSpeechParams"
        :speech-params="asrSpeechParams"
        :max-params="maxSpeechParams"
        class="vc-access-params"
      />
    </div>

    <MyLlmModelsModal
      v-model="myModelsModalVisible"
      :initial-tab="myLlmInitialTab"
      :triple-access="tripleAccess"
      :max-my-models="maxMyResources"
      @added="onCustomLlmModelAdded"
      @deleted="onCustomLlmModelDeleted"
      @updated="onCustomLlmModelUpdated"
    />
    <MyTtsResourcesModal
      v-model="myResourcesModalVisible"
      :initial-tab="myTtsInitialTab"
      :triple-access="tripleAccess"
      :max-my-resources="maxMyResources"
      @added="onCustomResourceAdded"
      @deleted="onCustomResourceDeleted"
      @updated="onCustomResourceUpdated"
    />
    <MyAsrResourcesModal
      v-model="myAsrResourcesModalVisible"
      :initial-tab="myAsrInitialTab"
      :triple-access="tripleAccess"
      :max-my-resources="maxMyResources"
      @added="onCustomAsrResourceAdded"
      @deleted="onCustomAsrResourceDeleted"
      @updated="onCustomAsrResourceUpdated"
    />
  </div>
</template>

<script>
import { getSpeechRecognitionEngineA, getTtsListA } from '@/server/api'
import MyAsrResourcesModal from './MyAsrResourcesModal.vue'
import MyLlmModelsModal from './MyLlmModelsModal.vue'
import MyTtsResourcesModal from './MyTtsResourcesModal.vue'
import SpeechParamsSection from './SpeechParamsSection.vue'
import {
  buildAsrEngineOptionsFromDictionaryList,
  buildCustomAsrResourceOptions,
  buildCustomLlmModelOptions,
  buildCustomTtsResourceOptions,
  buildTtsEngineOptionsFromDictionaryList,
  buildTtsMapFromDictionaryList,
  buildTripleTtsVoiceOptions,
  getLlmResourceConfig,
  isCustomTtsResourceEngine,
  normalizeTripleTtsEngine,
  resolveDefaultLlmResource,
  resolveDefaultTripleLlmResourceFromOptions,
  resolveDefaultTripleTtsEngineFromOptions,
  resolveTripleTtsVoiceId,
  sanitizeCustomAsrResources,
  sanitizeCustomLlmModels,
  splitTripleTtsVoiceOptions,
  sanitizeCustomTtsResources,
  syncLlmFieldsFromResource,
  syncTripleAsrEngineWithDictionary,
  SYSTEM_LLM_RESOURCES,
} from '../../utils/tripleAccessHelpers'

export default {
  name: 'LlmTripleAccessSection',
  components: {
    MyAsrResourcesModal,
    MyLlmModelsModal,
    MyTtsResourcesModal,
    SpeechParamsSection,
  },
  props: {
    tripleAccess: {
      type: Object,
      required: true,
    },
    clonedVoices: {
      type: Array,
      default: () => [],
    },
    ttsSpeechParams: {
      type: Array,
      default: null,
    },
    asrSpeechParams: {
      type: Array,
      default: null,
    },
    maxSpeechParams: {
      type: Number,
      default: 15,
    },
    maxMyResources: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      asrEngineOptions: [],
      asrListLoading: false,
      ttsMap: {},
      ttsEngineOptions: [],
      ttsListLoading: false,
      tripleTtsAddSpeakers: [],
      myResourcesModalVisible: false,
      myTtsInitialTab: 'add',
      myAsrResourcesModalVisible: false,
      myAsrInitialTab: 'add',
      myModelsModalVisible: false,
      myLlmInitialTab: 'add',
    }
  },
  computed: {
    asrDictionaryResourceOptions() {
      return Array.isArray(this.asrEngineOptions) ? this.asrEngineOptions : []
    },
    asrCustomResourceOptions() {
      return buildCustomAsrResourceOptions(this.tripleAccess.asr.custom_resources)
    },
    llmSystemResourceOptions() {
      return SYSTEM_LLM_RESOURCES
    },
    llmCustomModelOptions() {
      return buildCustomLlmModelOptions(this.tripleAccess.llm.custom_models)
    },
    llmModelSelectOptions() {
      const llm = this.tripleAccess.llm
      if (!llm || typeof llm !== 'object') return []
      const config = getLlmResourceConfig(llm.resource, llm.custom_models, this.llmSystemResourceOptions)
      const model = String((config && config.model) || (llm.model != null ? llm.model : '')).trim()
      if (!model) return []
      return [{ value: model, label: model }]
    },
    llmResourceOptions() {
      return [...this.llmSystemResourceOptions, ...this.llmCustomModelOptions]
    },
    isCustomTtsResource() {
      return isCustomTtsResourceEngine(this.tripleAccess.tts.engine)
    },
    ttsResourceOptions() {
      const dict = Array.isArray(this.ttsEngineOptions) ? this.ttsEngineOptions : []
      const custom = buildCustomTtsResourceOptions(this.tripleAccess.tts.custom_resources)
      return [...dict, ...custom]
    },
    ttsDictionaryResourceOptions() {
      return Array.isArray(this.ttsEngineOptions) ? this.ttsEngineOptions : []
    },
    ttsCustomResourceOptions() {
      return buildCustomTtsResourceOptions(this.tripleAccess.tts.custom_resources)
    },
    tripleTtsVoiceResolveOptions() {
      return {
        clonedVoices: this.clonedVoices,
        ttsMap: this.ttsMap,
        ttsEngine: this.tripleAccess.tts.engine,
        engineOptions: this.ttsEngineOptions,
      }
    },
    tripleTtsDictionarySpeakers() {
      if (this.isCustomTtsResource) return []
      const engine = normalizeTripleTtsEngine(
        this.tripleAccess.tts.engine,
        this.ttsMap,
        this.ttsEngineOptions,
      )
      return Array.isArray(this.ttsMap[engine]) ? this.ttsMap[engine] : []
    },
    tripleTtsVoiceOptions() {
      return buildTripleTtsVoiceOptions({
        ttsEngine: this.tripleAccess.tts.engine,
        ttsMap: this.ttsMap,
        clonedVoices: this.clonedVoices,
        selectedVoiceId: this.tripleAccess.tts.voice_id,
        engineOptions: this.ttsEngineOptions,
        extraSpeakers: this.tripleTtsAddSpeakers,
      })
    },
    tripleTtsSystemVoiceOptions() {
      return splitTripleTtsVoiceOptions(this.tripleTtsVoiceOptions, this.clonedVoices).system
    },
    tripleTtsClonedVoiceOptions() {
      return splitTripleTtsVoiceOptions(this.tripleTtsVoiceOptions, this.clonedVoices).mine
    },
  },
  watch: {
    tripleTtsDictionarySpeakers: {
      handler(speakers) {
        const list = Array.isArray(speakers) ? speakers : []
        for (let i = 0; i < list.length; i += 1) {
          const vocalName = list[i] && list[i].vocalName
          const index = this.tripleTtsAddSpeakers.findIndex((item) => item.vocalName === vocalName)
          if (index > -1) {
            this.tripleTtsAddSpeakers.splice(index, 1)
          }
        }
      },
    },
    clonedVoices: {
      deep: true,
      handler() {
        this.normalizeTripleTtsVoiceId()
      },
    },
    'tripleAccess.tts.voice_id': {
      immediate: true,
      handler(voiceId) {
        const val = String(voiceId != null ? voiceId : '').trim()
        if (!val) return
        const resolved = resolveTripleTtsVoiceId(val, this.tripleTtsVoiceResolveOptions)
        if (resolved && resolved !== val) {
          this.tripleAccess.tts.voice_id = resolved
          return
        }
        const exists = this.tripleTtsVoiceOptions.some((item) => item.value === val)
        if (!exists) {
          this.addTripleTtsSpeaker(val)
        }
      },
    },
  },
  created() {
    this.ensureCustomResourcesArray()
    this.ensureCustomAsrResourcesArray()
    this.ensureCustomLlmModelsArray()
    this.syncTripleLlmResourceWithOptions()
    this.fetchAsrEngines()
    this.fetchTtsList()
    this.normalizeTripleTtsVoiceId()
  },
  methods: {
    openMyTtsResources(tab = 'add') {
      this.myTtsInitialTab = tab === 'manage' ? 'manage' : 'add'
      this.myResourcesModalVisible = true
    },
    openMyLlmResources(tab = 'add') {
      this.myLlmInitialTab = tab === 'manage' ? 'manage' : 'add'
      this.myModelsModalVisible = true
    },
    openMyAsrResources(tab = 'add') {
      this.myAsrInitialTab = tab === 'manage' ? 'manage' : 'add'
      this.myAsrResourcesModalVisible = true
    },
    ensureCustomAsrResourcesArray() {
      if (!this.tripleAccess.asr || typeof this.tripleAccess.asr !== 'object') return
      if (!Array.isArray(this.tripleAccess.asr.custom_resources)) {
        this.$set(
          this.tripleAccess.asr,
          'custom_resources',
          sanitizeCustomAsrResources(this.tripleAccess.asr.custom_resources),
        )
      }
    },
    ensureCustomLlmModelsArray() {
      if (!this.tripleAccess.llm || typeof this.tripleAccess.llm !== 'object') return
      if (!Array.isArray(this.tripleAccess.llm.custom_models)) {
        this.$set(
          this.tripleAccess.llm,
          'custom_models',
          sanitizeCustomLlmModels(this.tripleAccess.llm.custom_models),
        )
      }
    },
    syncTripleLlmResourceWithOptions() {
      const llm = this.tripleAccess.llm
      if (!llm || typeof llm !== 'object') return

      const current = String(llm.resource != null ? llm.resource : '').trim()
      if (!current) {
        llm.resource = resolveDefaultLlmResource(llm)
      }

      const values = new Set(this.llmResourceOptions.map((item) => item.value))
      const resolved = String(llm.resource != null ? llm.resource : '').trim()
      if (resolved && !values.has(resolved)) {
        llm.resource = resolveDefaultTripleLlmResourceFromOptions(this.llmResourceOptions)
      } else if (!resolved && this.llmResourceOptions.length) {
        llm.resource = resolveDefaultTripleLlmResourceFromOptions(this.llmResourceOptions)
      }

      syncLlmFieldsFromResource(llm)
    },
    onLlmResourceChange() {
      syncLlmFieldsFromResource(this.tripleAccess.llm)
    },
    onCustomLlmModelAdded() {
      this.syncTripleLlmResourceWithOptions()
    },
    onCustomLlmModelDeleted() {
      this.syncTripleLlmResourceWithOptions()
    },
    onCustomLlmModelUpdated() {
      syncLlmFieldsFromResource(this.tripleAccess.llm)
    },
    ensureCustomResourcesArray() {
      if (!this.tripleAccess.tts || typeof this.tripleAccess.tts !== 'object') return
      if (!Array.isArray(this.tripleAccess.tts.custom_resources)) {
        this.$set(
          this.tripleAccess.tts,
          'custom_resources',
          sanitizeCustomTtsResources(this.tripleAccess.tts.custom_resources),
        )
      }
    },
    onTripleSelectOpenChange(open, refName) {
      if (!open) return
      this.$nextTick(() => {
        const select = this.$refs[refName]
        if (!select) return
        select.filterQueryChange = false
        select.isTyping = false
      })
    },
    normalizeTripleTtsVoiceId() {
      const raw = this.tripleAccess.tts.voice_id != null
        ? this.tripleAccess.tts.voice_id
        : this.tripleAccess.tts.voice
      const resolved = resolveTripleTtsVoiceId(raw, this.tripleTtsVoiceResolveOptions)
      if (resolved !== String(raw != null ? raw : '').trim()) {
        this.tripleAccess.tts.voice_id = resolved
      } else if (this.tripleAccess.tts.voice_id == null && raw != null) {
        this.tripleAccess.tts.voice_id = resolved
      }
      if (this.tripleAccess.tts.voice != null) {
        this.$delete(this.tripleAccess.tts, 'voice')
      }
    },
    fetchAsrEngines() {
      this.asrListLoading = true
      getSpeechRecognitionEngineA()
        .then((res) => {
          if (!res || res.code !== 200) return
          this.asrEngineOptions = buildAsrEngineOptionsFromDictionaryList(res.data)
          syncTripleAsrEngineWithDictionary(this.tripleAccess.asr, this.asrEngineOptions)
        })
        .catch(() => {})
        .finally(() => {
          this.asrListLoading = false
        })
    },
    fetchTtsList() {
      this.ttsListLoading = true
      getTtsListA()
        .then((res) => {
          if (!res || res.code !== 200) return
          this.ttsMap = buildTtsMapFromDictionaryList(res.data)
          this.ttsEngineOptions = buildTtsEngineOptionsFromDictionaryList(res.data)
          this.syncTripleTtsEngineWithDictionary()
          this.normalizeTripleTtsVoiceId()
        })
        .catch(() => {})
        .finally(() => {
          this.ttsListLoading = false
        })
    },
    syncTripleTtsEngineWithDictionary() {
      const cur = String(this.tripleAccess.tts.engine != null ? this.tripleAccess.tts.engine : '').trim()
      if (isCustomTtsResourceEngine(cur)) return

      const normalized = normalizeTripleTtsEngine(
        this.tripleAccess.tts.engine,
        this.ttsMap,
        this.ttsEngineOptions,
      )
      if (normalized !== this.tripleAccess.tts.engine) {
        this.tripleAccess.tts.engine = normalized
      }
      const values = new Set(this.ttsResourceOptions.map((item) => item.value))
      const current = String(this.tripleAccess.tts.engine != null ? this.tripleAccess.tts.engine : '').trim()
      if (current && values.has(current)) return
      if (!this.ttsEngineOptions.length) return
      this.tripleAccess.tts.engine = resolveDefaultTripleTtsEngineFromOptions(this.ttsEngineOptions)
    },
    onTtsEngineChange() {
      this.tripleTtsAddSpeakers = []
      const values = new Set(
        buildTripleTtsVoiceOptions({
          ttsEngine: this.tripleAccess.tts.engine,
          ttsMap: this.ttsMap,
          clonedVoices: this.clonedVoices,
          selectedVoiceId: '',
          engineOptions: this.ttsEngineOptions,
        }).map((item) => item.value),
      )
      const cur = resolveTripleTtsVoiceId(
        this.tripleAccess.tts.voice_id,
        this.tripleTtsVoiceResolveOptions,
      )
      if (cur && !values.has(cur)) {
        this.tripleAccess.tts.voice_id = ''
      }
    },
    addTripleTtsSpeaker(val) {
      const vocalName = String(val != null ? val : '').trim()
      if (!vocalName) return
      if (this.tripleTtsAddSpeakers.some((item) => item.vocalName === vocalName)) return
      this.tripleTtsAddSpeakers.push({ vocalName })
    },
    onCustomResourceAdded() {
      this.syncTripleTtsEngineWithDictionary()
      this.onTtsEngineChange()
    },
    onCustomResourceDeleted() {
      this.syncTripleTtsEngineWithDictionary()
      this.onTtsEngineChange()
    },
    onCustomResourceUpdated() {
      this.syncTripleTtsEngineWithDictionary()
    },
    onCustomAsrResourceAdded() {
      syncTripleAsrEngineWithDictionary(this.tripleAccess.asr, this.asrEngineOptions)
    },
    onCustomAsrResourceDeleted() {
      syncTripleAsrEngineWithDictionary(this.tripleAccess.asr, this.asrEngineOptions)
    },
    onCustomAsrResourceUpdated() {
      syncTripleAsrEngineWithDictionary(this.tripleAccess.asr, this.asrEngineOptions)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-triple-access {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.vc-access-block {
  border: 1px solid #e8eaec;
  border-radius: 8px;
  padding: 16px;
  background: #fff;

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 14px;
    text-align: left;
  }
}

.vc-access-fields-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}

.vc-access-field-block {
  min-width: 0;
}

.vc-form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #606266;
  line-height: 1.4;
  text-align: left;
}

.vc-form-control {
  width: 100%;

  ::v-deep &.ivu-select {
    width: 100%;
  }

  ::v-deep .ivu-select-selection,
  ::v-deep .ivu-select-selected-value,
  ::v-deep .ivu-select-input {
    text-align: left;
  }

  ::v-deep .ivu-select-arrow {
    right: 18px;
  }
}

.vc-resource-select-row,
.vc-voice-select-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  .vc-form-control {
    flex: 1;
    min-width: 0;
  }
}

.vc-access-fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.vc-access-field {
  display: flex;
  align-items: center;
  gap: 12px;
}

.vc-access-label {
  flex-shrink: 0;
  width: 72px;
  font-size: 13px;
  color: #606266;
  text-align: left;
}

.vc-access-control {
  flex: 1;
  min-width: 0;

  ::v-deep .ivu-select-selection,
  ::v-deep .ivu-select-selected-value,
  ::v-deep .ivu-select-input,
  ::v-deep .ivu-input {
    text-align: left;
  }

  ::v-deep .ivu-select-arrow {
    right: 18px;
  }
}

.vc-access-params {
  margin-top: 16px;
}
</style>

<style lang="scss">
.vc-grouped-select-transfer {
  .ivu-select-group-title {
    padding-left: 12px;
    font-size: 12px;
    font-weight: 400;
    color: #909399;
    line-height: 28px;
  }

  .ivu-select-item {
    padding: 7px 30px !important;
  }
}
</style>
